import React, { useRef, useState, useEffect } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import Header from './Header';
import MagnetButton from '../common/MagnetButton';

const HeroSection = () => {
  const containerRef = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const mouseXSpring = useSpring(x, { stiffness: 40, damping: 20 });
  const mouseYSpring = useSpring(y, { stiffness: 40, damping: 20 });

  const handleMouseMove = (e) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    x.set((e.clientX - rect.left - centerX) / centerX);
    y.set((e.clientY - rect.top - centerY) / centerY);
  };

  const handleConnectClick = () => {
    window.location.href = "mailto:fernando.comisnot@gmail.com";
  };

  const textX = useTransform(mouseXSpring, [-1, 1], [15, -15]);
  const textY = useTransform(mouseYSpring, [-1, 1], [15, -15]);
  const tagsX = useTransform(mouseXSpring, [-1, 1], [-25, 25]);
  const tagsY = useTransform(mouseYSpring, [-1, 1], [-25, 25]);

  const tags = [
    { text: "< HR & Management />", className: "top-[22%] left-[3%] md:left-[12%]" },
    { text: "< Web Development />", className: "top-[28%] right-[3%] md:right-[12%]" },
    { text: "< Branding & Content Creation />", className: "top-[48%] left-[2%] md:left-[6%]" },
    { text: "< Deployment & Hosting />", className: "top-[52%] right-[2%] md:right-[6%]" },
    { text: "< Digital Marketing />", className: "bottom-[32%] left-[3%] md:left-[12%]" },
    { text: "< Social Media Management />", className: "bottom-[28%] right-[3%] md:right-[12%]" },
  ];

  return (
    <section 
      ref={containerRef} 
      onMouseMove={handleMouseMove} 
      id="home" 
      className="relative h-screen bg-[#050505] flex flex-col items-center justify-center overflow-hidden"
    >
      <Header />
      
      <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-blue-900/10 rounded-full blur-[120px] -translate-x-1/2 -translate-y-1/2 pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-purple-900/10 rounded-full blur-[120px] translate-x-1/2 translate-y-1/2 pointer-events-none" />

      <div className="relative w-full max-w-[95vw] mx-auto h-full flex flex-col items-center justify-center">
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ delay: 0.2 }}
          className="absolute top-[15vh] left-0 right-0 text-center z-20"
        >
          <span className="text-gray-400 text-sm md:text-base tracking-[0.3em] uppercase font-medium">
            Hello, I'm Fernando
          </span>
        </motion.div>

        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-max pointer-events-none select-none z-0 flex justify-center items-center">
          <motion.h1 
            style={{ x: textX, y: textY }}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1 }}
            className="text-[15vw] md:text-[11vw] font-black leading-[0.8] text-[#1a1a1a] text-center flex flex-col items-center"
          >
            <span>MANAGEMENT</span>
            <span>EXECUTIVE</span>
          </motion.h1>
        </div>

        <div className="relative z-10 flex items-center justify-center h-full w-full pointer-events-none">
          <SpotlightImage />
        </div>

        {tags.map((tag, i) => (
          <motion.div
            key={i}
            style={{ x: tagsX, y: tagsY }}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4 + (i * 0.1), duration: 0.6 }}
            className={`absolute ${tag.className} z-30 pointer-events-none hidden md:block`}
          >
            <span className="text-gray-500/90 text-xs md:text-sm lg:text-base font-medium tracking-wider whitespace-nowrap font-mono bg-black/20 backdrop-blur-sm px-3 py-1 rounded-full border border-white/5">
              {tag.text}
            </span>
          </motion.div>
        ))}

        <div className="absolute bottom-[22vh] md:bottom-[5vh] left-1/2 -translate-x-1/2 z-20 pointer-events-none flex justify-center w-max">
          <motion.h2 
            style={{ x: textX, y: textY }}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="text-[13vw] md:text-[14vw] font-black leading-none tracking-tighter text-transparent bg-clip-text bg-gradient-to-b from-white via-gray-400 to-gray-700 whitespace-nowrap text-center"
          >
            WEB DEVELOPER
          </motion.h2>
        </div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ delay: 0.6 }}
          className="absolute bottom-8 left-0 w-full px-6 z-40 md:hidden pointer-events-auto"
        >
          <MagnetButton 
            onClick={handleConnectClick} 
            className="group relative flex items-center justify-center w-full py-4 rounded-full border border-white/20 text-white hover:bg-white hover:text-black transition-all duration-300 overflow-hidden"
          >
            <span className="relative z-10 text-sm font-medium uppercase tracking-widest">Connect</span>
          </MagnetButton>
        </motion.div>
      </div>
    </section>
  );
};

const SpotlightImage = () => {
  const imgRef = useRef(null);
  const [cursor, setCursor] = useState({ x: -1000, y: -1000 });
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleMouseMove = (e) => {
    if (imgRef.current && !isMobile) {
      const rect = imgRef.current.getBoundingClientRect();
      setCursor({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    }
  };

  const imageUrl = "https://media-manager-c.questera.ai/greta-media/ab03ff55dc7bcdd9e4a34a93308a2aa656b3e0baf34844f0be8f244dd21175025656600d9573a40a26a1591403a49a1d/images/aW1hZ2UvcG5n/aaa104141c57ea6abe64316012ca9568.png";

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
      className="relative h-[60vh] md:h-[88vh] w-auto aspect-[4/5] pointer-events-auto"
      onMouseMove={handleMouseMove}
      onMouseLeave={() => setCursor({ x: -1000, y: -1000 })}
      ref={imgRef}
      style={{ 
        maskImage: 'linear-gradient(to bottom, black 80%, transparent 100%)',
        WebkitMaskImage: 'linear-gradient(to bottom, black 80%, transparent 100%)'
      }}
    >
      <img src={imageUrl} alt="Fernando Profile" className={`w-full h-full object-contain filter grayscale brightness-[0.25] ${isMobile ? 'hidden' : 'block'}`} />
      <img 
        src={imageUrl} 
        alt="Fernando Profile Color" 
        className="absolute inset-0 w-full h-full object-contain" 
        style={!isMobile ? {
          maskImage: `radial-gradient(circle 140px at ${cursor.x}px ${cursor.y}px, black, transparent)`,
          WebkitMaskImage: `radial-gradient(circle 140px at ${cursor.x}px ${cursor.y}px, black, transparent)`,
        } : { opacity: 1 }}
      />
    </motion.div>
  );
};

export default HeroSection;